import appTheme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useState } from 'react';

import { useAppSelector } from '@/redux/hooks';
import {
    getUiForgotPasswordError,
    getUiForgotPasswordErrorMessage,
    getUiForgotPasswordSubmitted,
} from '@/redux/modules/forgotPassword';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import { required, email as validateEmail } from '@/utils/validation';

type Props = {
    submitForgotPassword: (email: string, sendSMS: boolean) => void;
};

const ForgotPassword = ({ submitForgotPassword }: Props) => {
    const { formatMessage } = useIntl();
    const [email, setEmail] = useState<string>('');
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [sendSMS, setSendSMS] = useState<boolean>(false);

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);
    const forgotPasswordSubmitted = useAppSelector(getUiForgotPasswordSubmitted);
    const forgotPasswordError = useAppSelector(getUiForgotPasswordError);
    const forgotPasswordErrorMessage = useAppSelector(getUiForgotPasswordErrorMessage);

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const onSubmit = () => {
        const trimmedEmail = email.trim();
        const newEmailValid = required(trimmedEmail) && validateEmail(trimmedEmail);
        setEmailValid(newEmailValid);

        if (newEmailValid) {
            submitForgotPassword(trimmedEmail, sendSMS);
            setEmail(trimmedEmail);
        }
    };

    const handleEnterKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            onSubmit();
        }
        event.stopPropagation();
    };

    const hasError = forgotPasswordError || !emailValid;

    return (
        <>
            <TextInput
                aria-label={formatMessage({ id: 'forgotPassword.emailAddress' })}
                autoComplete="email"
                data-testid="forgotPasswordModalEmail"
                errorText={forgotPasswordErrorMessage || String(formatMessage({ id: 'forgotPassword.error' }))}
                hasError={hasError}
                id="email"
                labelText={formatMessage({ id: 'forgotPassword.emailAddress' })}
                onChange={onEmailChange}
                onKeyUp={handleEnterKeyUp}
                type="text"
            />
            <Checkbox.Option
                className={cn({ 'mt-xl': forgotPasswordError && emailValid })}
                checked={sendSMS}
                name="sendSMSCheckbox"
                onChange={() => setSendSMS(!sendSMS)}
            >
                {formatMessage({ id: 'forgotPassword.sendResetLinkToSMS' })}
            </Checkbox.Option>
            <div className="flex w-full justify-center">
                <Button
                    className="mt-md w-[264px] xsMax:w-full"
                    data-testid="forgotPasswordModalSendReset"
                    disabled={forgotPasswordSubmitted}
                    loading={forgotPasswordSubmitted}
                    onClick={onSubmit}
                    style={!isWhiteLabel || whiteLabelPrimaryColor === appTheme.colors.blue200 ? 'critical' : 'primary'}
                    variant="filled"
                >
                    {formatMessage({ id: 'forgotPassword.submit' })}
                </Button>
            </div>
        </>
    );
};

export default ForgotPassword;
